import React, { useRef, useState } from 'react';
import {
  Text,
  Group,
  Paper,
  Badge,
  ActionIcon,
  Box,
  TextInput,
} from '@mantine/core';
import {
  IconCheck,
  IconPlus,
  IconHome,
  IconX,
  IconCarrot,
  IconTagStarred,
  IconWorldStar,
  IconCalendarStar,
} from '@tabler/icons-react';
import { MealPlan, GroceryItem } from '../../types/mealPlan';
import { getAssignmentRecipe } from '../../utils/mealPlanHelpers';
import { PantryItem } from '../../types/pantry';
import { formatQuantity } from '../../utils/formatQuantity';
import { normalizeItemName } from '../../utils/itemNormalization';
import { GroceryItemActions } from './GroceryItemActions';
import { SplitButton } from '../UI/SplitButton';
import { useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { useDebouncedCallback } from '@mantine/hooks';

type GroceryItemCardProps = {
  item: GroceryItem;
  mealPlan: MealPlan;
  planId: string;
  pantryItems: PantryItem[];
  fetchPantryItems: () => void;
  setGroceryList: React.Dispatch<React.SetStateAction<GroceryItem[]>>;
  isOwner: boolean;
  navigate: any;
  krogerConnected: boolean;
  onToggle: () => void;
  preferenceExpanded: boolean;
  // Host renders the add-to-pantry form itself (pushed drawer view)
  onAddWithQuantity?: () => void;
  handleToggleProductPreference: (itemName: string) => void;
  handleSaveProductPreference: (itemName: string, preference: string, scope?: 'meal_plan' | 'ingredient' | 'category' | 'global') => void;
};

export const GroceryItemCard = ({
  item,
  mealPlan,
  planId,
  pantryItems,
  fetchPantryItems,
  setGroceryList,
  isOwner,
  navigate,
  krogerConnected,
  onToggle,
  preferenceExpanded,
  onAddWithQuantity,
  handleToggleProductPreference,
  handleSaveProductPreference,
}: GroceryItemCardProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // This card renders inside the shopping list drawer; when that drawer is a
  // bottom sheet, menus must not portal to the body (mispositioning jank).
  const isBottomSheet = useIsBottomSheet();
  // Draft mirrors the preference input just for the save button's disabled
  // state; the save handlers read the authoritative value from the ref.
  const [draftPreference, setDraftPreference] = useState('');
  const debouncedSetDraft = useDebouncedCallback((value: string) => setDraftPreference(value), 150);

  const formattedQuantity = formatQuantity(item.totalQuantity, item.unit);
  const pantryItem = pantryItems.find(pantry =>
    pantry.name === normalizeItemName(item.itemName)
  );
  const saveDisabled = !item.productPreference && !draftPreference.trim();

  return (
    <Paper
      p="sm"
      radius="lg"
      className={`grocery-item ${preferenceExpanded ? 'preference-expanded' : ''}`}
      style={{
        backgroundColor: item.alreadyHave ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-white)',
        border: '1px solid var(--mantine-color-gray-2)',
        cursor: isOwner ? 'pointer' : 'default',
        transition: 'background-color 0.1s ease',
        position: 'relative',
        minHeight: '80px'
      }}
      onClick={isOwner ? onToggle : undefined}
    >
      {/* Dim the content, not the card: opacity on the Paper creates a
          stacking context that traps the inline kebab menu underneath the
          next sibling card */}
      <Group gap="sm" align="flex-start" style={{ opacity: item.alreadyHave ? 0.6 : 1 }}>
        <Box style={{ marginTop: 2 }}>
          {item.alreadyHave ?
            <IconCheck size={18} color="var(--mantine-color-green-6)" /> :
            <IconPlus size={18} color="var(--mantine-color-gray-5)" />
          }
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          {/* Floated spacer reserving the top-right corner for the
              absolutely-positioned actions (kebab / pantry badge) so the
              item name wraps beside them then flows full width beneath,
              instead of every line being crammed into a narrow column. */}
          <Box
            aria-hidden
            style={{ float: 'right', width: 60, height: 34, pointerEvents: 'none' }}
          />
          <Text
            fw={500}
            size="sm"
            style={{
              textDecoration: item.alreadyHave ? 'line-through' : 'none',
              lineHeight: 1.3
            }}
          >
            {item.itemName}
            {formattedQuantity !== '' && (
              <Text span fw={400} c="dimmed"> ({formattedQuantity})</Text>
            )}
            {item.optional && <Text span c="dimmed" fw={400}> (optional)</Text>}
          </Text>
          <Box mt={2} style={{ paddingRight: '10px' }} pr={{ sm: '30px' }}>
            {(() => {
              const recipes = item.usedInRecipes;

              if (recipes.length <= 3) {
                // Show each recipe on its own line for better mobile readability
                return recipes.map((recipe, idx) => (
                  <Text key={idx} size="xs" c="dimmed" style={{ lineHeight: 1.3 }}>
                    {recipe}
                  </Text>
                ));
              } else {
                // For 4+ recipes, show first 3 and count
                return (
                  <>
                    {recipes.slice(0, 3).map((recipe, idx) => (
                      <Text key={idx} size="xs" c="dimmed" style={{ lineHeight: 1.3 }}>
                        {recipe}
                      </Text>
                    ))}
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.3, fontStyle: 'italic' }}>
                      +{recipes.length - 3} more recipe{recipes.length - 3 === 1 ? '' : 's'}
                    </Text>
                  </>
                );
              }
            })()}
          </Box>

          {/* Pantry indicator below recipes on mobile only */}
          {pantryItem && (
            <Box
              mt="xs"
              style={{ textAlign: 'left' }}
              hiddenFrom="sm"
            >
              <Badge
                size="sm"
                variant="light"
                color="green"
                leftSection={<IconHome size={12} />}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/pantry?search=${encodeURIComponent(item.itemName)}`);
                }}
              >
                {pantryItem.type === 'staple'
                  ? 'In Pantry'
                  : `${formatQuantity(pantryItem.quantity || 0, pantryItem.unit || '')} in pantry`
                }
              </Badge>
            </Box>
          )}

          {/* Days indicator below the pantry badge */}
          <Box mt="xs">
            {(() => {
              // Get unique days for this ingredient
              const days = item.usedInRecipes.map(recipeName => {
                const assignment = mealPlan.assignments.find(a => getAssignmentRecipe(a, mealPlan)?.name === recipeName);
                return assignment?.plannedDay;
              }).filter(Boolean) as string[];

              const uniqueDays = Array.from(new Set(days));
              // Sort by the actual planned date order instead of hardcoded Mon-Sun
              const dayAbbrevs = uniqueDays
                .map(day => {
                  const assignment = mealPlan.assignments.find(a => a.plannedDay === day);
                  return {
                    day: day,
                    abbrev: day.slice(0, 3),
                    singleLetter: day.slice(0, 1),
                    date: assignment?.plannedDate || ''
                  };
                })
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

              return dayAbbrevs.length > 0 ? (
                <Text
                  size="xs"
                  c="dimmed"
                  fw={500}
                  style={{
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    fontStyle: 'italic'
                  }}
                >
                  {dayAbbrevs.map(d => d.abbrev).join(' ')}
                </Text>
              ) : null;
            })()}
          </Box>

          {/* Show product preference if set, input is not expanded, and Kroger is connected */}
          {item.productPreference && !preferenceExpanded && krogerConnected && (
            <Group gap={4} mt={8} style={{ paddingRight: '50px' }}>
              {(() => {
                const scope = item.productPreferenceScope;
                if (scope === 'global') {
                  return <IconWorldStar size={14} style={{ color: '#134B97', opacity: 0.7 }} />;
                } else if (scope === 'category') {
                  return <IconTagStarred size={14} style={{ color: '#134B97', opacity: 0.7 }} />;
                } else if (scope === 'ingredient') {
                  return <IconCarrot size={14} style={{ color: '#134B97', opacity: 0.7 }} />;
                } else {
                  return <IconCalendarStar size={14} style={{ color: '#134B97', opacity: 0.7 }} />;
                }
              })()}
              <Text size="xs" style={{
                fontStyle: 'italic',
                lineHeight: 1.2,
                color: '#134B97',
                opacity: 0.7,
              }}>
                {item.productPreference}
              </Text>
            </Group>
          )}
        </Box>
      </Group>

      {/* Pantry actions or pantry indicator in top right corner */}
      <Box
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
        }}
      >
        {pantryItem ? (
          // Show "In Pantry" badge on desktop only
          <Badge
            size="sm"
            variant="light"
            color="green"
            leftSection={<IconHome size={12} />}
            visibleFrom="sm"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pantry?search=${encodeURIComponent(item.itemName)}`);
            }}
          >
            {pantryItem.type === 'staple'
              ? 'In Pantry'
              : `${formatQuantity(pantryItem.quantity || 0, pantryItem.unit || '')} in pantry`
            }
          </Badge>
        ) : (
          // Show GroceryItemActions for adding to pantry and shopping preferences - hide when preference input is expanded
          isOwner && !preferenceExpanded ? (
            <Group
              gap="xs"
              className="pantry-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <GroceryItemActions
                itemName={item.itemName}
                quantity={item.totalQuantity}
                unit={item.unit}
                category={item.category}
                mealPlanId={planId}
                requiredQuantity={item.totalQuantity}
                requiredUnit={item.unit}
                onSuccess={() => {
                  // Refresh pantry data when item is added
                  fetchPantryItems();
                }}
                onGroceryListUpdate={(updatedList) => {
                  setGroceryList(updatedList);
                }}
                hasProductPreference={!!item.productPreference}
                isKrogerConnected={krogerConnected}
                cartAdditionCompleted={mealPlan?.cartAdditionStatus?.status === 'completed'}
                onToggleProductPreference={handleToggleProductPreference}
                alreadyHave={item.alreadyHave}
                menuWithinPortal={!isBottomSheet}
                onAddWithQuantity={onAddWithQuantity}
              />
            </Group>
          ) : null
        )}
      </Box>

      {/* Product Preference Input - Only show if Kroger is connected */}
      {preferenceExpanded && krogerConnected && (
        <Box mt="sm" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <Text size="xs" c="dimmed" mb="xs" style={{ lineHeight: 1.4 }}>
            <Text component="span">Add specific shopping preferences for this ingredient like brand, quality, nutrition, size, etc.</Text>
          </Text>
          <Group gap="xs" align="stretch">
            <TextInput
              ref={inputRef}
              placeholder="Heinz, low fat, buy in bulk..."
              size="sm"
              styles={{
                // 16px font: anything smaller makes mobile browsers auto-zoom
                // into the input on focus, janking the bottom sheet
                input: { height: '40px', minHeight: '40px', fontSize: '16px' }
              }}
              style={{ flex: 1, maxWidth: '450px' }}
              defaultValue={item.productPreference || ''}
              maxLength={100}
              onChange={(e) => {
                // Only debounce the draft update for button state
                debouncedSetDraft(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveProductPreference(item.itemName, e.currentTarget.value);
                }
                if (e.key === 'Escape') {
                  handleToggleProductPreference(item.itemName);
                }
              }}
            />
            <SplitButton
              mainAction={{
                label: "",
                leftSection: <IconCheck size={16} />,
                onClick: () => {
                  handleSaveProductPreference(
                    item.itemName,
                    inputRef.current?.value || '',
                    'meal_plan'
                  );
                }
              }}
              menuItems={[
                {
                  label: "Remember for this ingredient",
                  leftSection: <IconCarrot size={16} />,
                  onClick: () => {
                    handleSaveProductPreference(
                      item.itemName,
                      inputRef.current?.value || '',
                      'ingredient'
                    );
                  }
                },
                {
                  label: "Remember for this category",
                  leftSection: <IconTagStarred size={16} />,
                  onClick: () => {
                    handleSaveProductPreference(
                      item.itemName,
                      inputRef.current?.value || '',
                      'category'
                    );
                  },
                  disabled: !item.category // Only enable if item has a category
                },
                {
                  label: "Remember for everything",
                  leftSection: <IconWorldStar size={16} />,
                  onClick: () => {
                    handleSaveProductPreference(
                      item.itemName,
                      inputRef.current?.value || '',
                      'global'
                    );
                  }
                }
              ]}
              disabled={saveDisabled}
              menuWithinPortal={!isBottomSheet}
              style={{
                backgroundColor: saveDisabled ? '#e9ecef' : '#134B97',
              }}
            />
            <ActionIcon
              size={40}
              variant="light"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleProductPreference(item.itemName);
              }}
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>
        </Box>
      )}
    </Paper>
  );
};
